import { useId, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@libs/zapi-ui/ds/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { Field, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput
} from "@libs/zapi-ui/ds/components/input-group";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { CaretDownIcon, CheckIcon, DownloadSimpleIcon } from "@phosphor-icons/react";
import { exportProject, type TExportProjectOptions } from "../lib/pdf-export";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { usePresentationStore } from "@/zapi-frontend/stores/presentation";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { ExportOptionsModal } from "./_ExportOptionsModal";
import $api from "@/zapi-frontend/shared/api/axios";
import { useQueryClient } from "@tanstack/react-query";
import { useEditorStore } from "@/zapi-frontend/stores/editor";

const PRESENTATION_FORMATS = ["pdf", "pptx"] as const;
type TPresentationFormat = (typeof PRESENTATION_FORMATS)[number];

const sanitizeFileName = (value: string) => value.replace(/[^\p{L}\p{N}_\-\s]/gu, "");

export function ExportButton({ disabled = false }: { disabled?: boolean }) {
  const { t } = useTranslation(["projectHome", "common"]);
  const [searchParams] = useSearchParams();
  const isPresentationTab = searchParams.get("tab") === "presentation";
  const queryClient = useQueryClient();

  const { selectedProject, isFetchProjectLoading } = useProjectStore();
  const { presentation } = usePresentationStore();
  const { flushSave } = useEditorStore();
  const [isExporting, setIsExporting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [presentationModalOpen, setPresentationModalOpen] = useState(false);
  const [presentationFileName, setPresentationFileName] = useState("");
  const [presentationFileType, setPresentationFileType] = useState<TPresentationFormat>("pdf");
  const presentationFileNameId = useId();

  const presentationFileUrl = (format: TPresentationFormat) =>
    format === "pdf" ? presentation?.gammaPdfUrl : presentation?.gammaPptxUrl;
  const availablePresentationFormats = PRESENTATION_FORMATS.filter((format) => presentationFileUrl(format));

  const handleExport = async (options: TExportProjectOptions) => {
    setModalOpen(false);
    setIsExporting(true);
    const toastId = toast.loading(t("common:exporting"));
    try {
      await flushSave?.();
      await exportProject(selectedProject?.id as unknown as number, options);

      await queryClient.invalidateQueries({
        queryKey: ["exportHistory", selectedProject?.id.toString()]
      });

      toast.success(t("header.exportDocSuccess"), { id: toastId });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("header.exportDocFailed"), { id: toastId });
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const openPresentationModal = () => {
    setPresentationFileName(sanitizeFileName(selectedProject?.name || "presentation").trim());
    setPresentationFileType(availablePresentationFormats[0] ?? "pdf");
    setPresentationModalOpen(true);
  };

  const handlePresentationExport = async () => {
    if (!selectedProject?.id) return;

    const sourceUrl = presentationFileUrl(presentationFileType);
    if (!sourceUrl) {
      toast.error(t("header.exportFormatUnavailable", { format: presentationFileType.toUpperCase() }));
      return;
    }

    try {
      setIsExporting(true);
      const finalName = presentationFileName.trim() || "presentation";
      const response = await $api.get<{ data: { url: string } }>(
        `/projects/${selectedProject.id}/presentations/download-url`,
        { params: { fileType: presentationFileType, fileName: finalName } }
      );

      const signedUrl = response.data.data.url;
      const fileResponse = await fetch(signedUrl);
      const blob = await fileResponse.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${finalName}.${presentationFileType}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      setPresentationModalOpen(false);

      await queryClient.invalidateQueries({
        queryKey: ["exportHistory", selectedProject.id.toString()]
      });
    } catch (error) {
      console.error("Download error:", error);
      toast.error(t("header.exportDownloadFailed"));
    } finally {
      setIsExporting(false);
    }
  };

  const hasPresentationFiles = availablePresentationFormats.length > 0;
  const isPresentationExportDisabled = disabled || !hasPresentationFiles || isFetchProjectLoading || isExporting;
  const isDocumentExportDisabled = disabled || isExporting || isFetchProjectLoading;

  if (isPresentationTab) {
    return (
      <>
        <Button
          type="button"
          variant="secondary"
          title={t("header.exportPresentation")}
          disabled={isPresentationExportDisabled}
          onClick={openPresentationModal}
          data-testid="export-menu-trigger"
        >
          <DownloadSimpleIcon className="size-4" />
          <FigmaTypography variant="label-2" color={isPresentationExportDisabled ? "zinc400" : "zinc700"}>
            {t("common:exportAs")}
          </FigmaTypography>
        </Button>

        <Dialog open={presentationModalOpen} onOpenChange={(open) => !isExporting && setPresentationModalOpen(open)}>
          <DialogContent closeLabel={t("common:close")} className="gap-5 rounded-2xl sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="typo-h5">{t("common:export")}</DialogTitle>
            </DialogHeader>
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (isExporting || !presentationFileName.trim()) return;
                void handlePresentationExport();
              }}
            >
              <Field>
                <FieldLabel htmlFor={presentationFileNameId}>{t("common:fileName")}</FieldLabel>
                <InputGroup data-disabled={isExporting || undefined}>
                  <InputGroupInput
                    id={presentationFileNameId}
                    value={presentationFileName}
                    onChange={(e) => setPresentationFileName(sanitizeFileName(e.target.value))}
                    placeholder="presentation"
                    disabled={isExporting}
                    data-testid="presentation-filename-input"
                  />
                  <InputGroupAddon align="inline-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        data-testid="presentation-format-trigger"
                        disabled={isExporting}
                        render={<InputGroupButton />}
                      >
                        .{presentationFileType}
                        <CaretDownIcon />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" sideOffset={8} className="w-auto min-w-48 rounded-xl p-1.5">
                        {availablePresentationFormats.map((type) => (
                          <DropdownMenuItem
                            key={type}
                            data-testid={`presentation-format-${type}`}
                            onClick={() => setPresentationFileType(type)}
                          >
                            <CheckIcon className={presentationFileType === type ? "opacity-100" : "opacity-0"} />.{type}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </InputGroupAddon>
                </InputGroup>
              </Field>
              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  shape="pill"
                  data-testid="presentation-export-cancel-button"
                  onClick={() => setPresentationModalOpen(false)}
                  disabled={isExporting}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  type="submit"
                  shape="pill"
                  disabled={isExporting || !presentationFileName.trim()}
                  data-testid="presentation-export-submit"
                >
                  {isExporting && <Spinner data-icon="inline-start" aria-label={t("common:loading")} />}
                  {isExporting ? t("common:exporting") : t("common:export")}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        title={t("header.exportProject")}
        disabled={isDocumentExportDisabled}
        onClick={() => setModalOpen(true)}
        data-testid="export-menu-trigger"
      >
        <DownloadSimpleIcon className="size-4" />
        <FigmaTypography variant="label-2" color={isDocumentExportDisabled ? "zinc400" : "zinc700"}>
          {t("common:exportAs")}
        </FigmaTypography>
      </Button>

      <ExportOptionsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onExport={handleExport}
        isExporting={isExporting}
        projectName={selectedProject?.name || ""}
      />
    </>
  );
}
